<script lang="ts" setup>
import {ConfigProvider} from 'ant-design-vue';
import {Locale} from 'ant-design-vue/lib/locale-provider';
import {onMounted, onUnmounted, provide, ref, Ref} from 'vue';
import {useRouter} from 'vue-router';
import {DEFAULT_LANG} from '../../app.const';
import {AuthService} from '../../auth/auth.service';
import Auth from '../../auth/Auth.vue';
import {eventBus} from '../../core/common/event-bus';
import dayjs from '../../core/dayjs';
import {changeLanguage, getStrings, overrideTranslations} from '../../core/lang';
import {useService} from '../../core/useService';
import providers from '../../providers';
import initTranslations from '../../translations';
import {AccountSettingsService} from '../settings/account-settings/account-settings.service';
import {MaxoptraAppContext, provideAppContext, useAppContext} from './AppContext';
import {RoutesService} from './routes.service';
import UseTheme from './UseTheme.vue';
import {IconDefaultImage} from '../icons';
import {KeyValueStorageService} from '../../services/common/key-value-storage.service';
import WootricProvider from './WootricProvider.vue';
import {startWootric} from '../../core/composable/useWootric';
initTranslations();
const authService = useService(AuthService);
const accountSettingsService = useService(AccountSettingsService);
const router = useRouter();
const keyValueStorageService = useService(KeyValueStorageService);
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    try {
        window.sessionStorage?.setItem('initialURL', document.location.hash.replace('#', ''));
    } catch {
        // ignore storage issues in non-browser/test envs
    }
}

let loadedTimeout: any;
onMounted(() => {
    loadedTimeout = setTimeout(() => {
        if (typeof document !== 'undefined') {
            document.body.setAttribute('loaded', '');
        }
    }, 500);
    eventBus.on('SETTINGS_UPDATE', () => updateSettings());
});

onUnmounted(() => {
    if (loadedTimeout) {
        clearTimeout(loadedTimeout);
        loadedTimeout = null;
    }
});
providers.forEach((resource) => {
    provide(resource.name, resource);
});
const S: CommonStrings = getStrings();

const antDesignLocaleMap = {
    en: () => import('ant-design-vue/es/locale/en_GB'),
    uk: () => import('ant-design-vue/es/locale/en_GB'),
    ru: () => import('ant-design-vue/es/locale/ru_RU'),
    es: () => import('ant-design-vue/es/locale/es_ES'),
    pt: () => import('ant-design-vue/es/locale/pt_PT'),
    fr: () => import('ant-design-vue/es/locale/fr_FR'),
    us: () => import('ant-design-vue/es/locale/en_US')
};

const locale: Ref<Locale> = ref(null);
const ready: Ref<boolean> = ref(false);
const context: MaxoptraAppContext = {};

async function loadFonts() {
    if (typeof document !== 'undefined' && (document as any).fonts) {
        (document as any).fonts.load('300 11px Lato');
        (document as any).fonts.load('400 11px Lato');
        (document as any).fonts.load('700 11px Lato');
        await (document as any).fonts.ready;
    }
}
async function updateSettings() {
    // TODO update settings
    // const settings = await authService.loadSettings();
    // Object.assign(context.settings, settings);
    // overrideTranslations({
    //     capacityUnits: {[DEFAULT_LANG]: context.settings.capacityUnits},
    //     volumeUnits: {[DEFAULT_LANG]: context.settings.volumeUnits},
    //     volumeLabel: {[DEFAULT_LANG]: context.settings.volumeLabel},
    //     capacityLabel: {[DEFAULT_LANG]: context.settings.capacityLabel}
    // });
}

async function onLoggedIn() {
    // batch loading of all resources
    const user = await authService.loadUser();
    const orderMetadata = await authService.loadOrderMetadata();
    const settings = await accountSettingsService.getAppSettings();
    await loadFonts();
    context.user = user;
    context.language = context.user.language.code;
    context.settings = settings;
    context.uiSettings = await authService.uiSettings(context.user.account.url);
    changeLanguage(context.language);
    context.orderMetadata = authService.traslateMetadata(orderMetadata);
    provideAppContext(context);
    useAppContext().settings = await accountSettingsService.getSettings();
    context.settings.isRestrictedDispatcherSettings = await keyValueStorageService.getBooleanValue(
        `isRestrictedDispatcherSettings_a${context.user.account.id}`,
        false
    );
    useAppContext().settings.isRestrictedDispatcherSettings = context.settings.isRestrictedDispatcherSettings;

    // provide correct capacity and volume units
    overrideTranslations({
        capacityUnits: {[DEFAULT_LANG]: context.settings.capacityUnits},
        volumeUnits: {[DEFAULT_LANG]: context.settings.volumeUnits},
        volumeLabel: {[DEFAULT_LANG]: context.settings.volumeLabel},
        capacityLabel: {[DEFAULT_LANG]: context.settings.capacityLabel}
    });

    // ant design locale
    locale.value = (
        await (antDesignLocaleMap[context.settings?.locale?.toLowerCase() || 'en'] || antDesignLocaleMap.en)()
    ).default;
    // dayjs locale
    dayjs.locale(locale.value.locale);

    useAppContext().dateFormats = {
        date: dayjs.localeData().longDateFormat('L'),
        time: 'HH:mm',
        datetime: dayjs.localeData().longDateFormat('L') + ' HH:mm'
    };

    useService(RoutesService).addRoutes(user.currentRole);
    ready.value = true;
    await goToDefaultPage(context);
    startWootric();
}

async function goToDefaultPage(context: MaxoptraAppContext) {
    if (!DISABLE_ROUTER) {
        await router.replace(useService(RoutesService).getDefaultUrl(context.user)).catch(() => {
            console.error('Error when redirect to url');
        });
    }
}
</script>
<template>
    <UseTheme :theme="false ? 'dark' : 'main'">
        <ConfigProvider :locale="locale">
            <template #renderEmpty>
                <div style="text-align: center">
                    <IconDefaultImage />
                    <p>{{ S.gridNoData }}</p>
                </div>
            </template>
            <div class="flex flex-col h-full overflow-hidden">
                <template v-if="ready">
                    <WootricProvider />
                    <slot></slot>
                </template>
                <Auth v-else @success="onLoggedIn"></Auth>
            </div>
        </ConfigProvider>
    </UseTheme>
</template>
<style lang="less" scoped></style>
